import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { submitIntradayTrade } from "../services/scannerService";

interface IntradayTradeModalProps {
  open: boolean;
  onClose: () => void;
  stock: any;
  baseRisk: number;
  livePrice: number;
}

export default function IntradayTradeModal({
  open,
  onClose,
  stock,
  baseRisk,
  livePrice,
}: IntradayTradeModalProps) {
  const [quantity, setQuantity] = useState(0);
  const [stopLoss, setStopLoss] = useState(0);
  const [gttStopLoss, setGttStopLoss] = useState(0);
  const [riskPerTrade, setRiskPerTrade] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (stock && livePrice) {
      // 1. Calculate risk selection based on score
      const calculatedRisk = baseRisk * (stock.score / 100);
      setRiskPerTrade(calculatedRisk);

      // 2. Default SL is ORL
      const initialSL = stock.orl || livePrice * 0.99;
      setStopLoss(Number(initialSL.toFixed(2)));

      // 3. Disaster GTT is 2x SL distance or 2% below ORL
      const slPoints = livePrice - initialSL;
      const initialGTT = initialSL - slPoints; // 2x SL distance
      setGttStopLoss(Number(initialGTT.toFixed(2)));

      // 4. Calculate Quantity
      if (slPoints > 0) {
        setQuantity(Math.floor(calculatedRisk / slPoints));
      } else {
        setQuantity(1);
      }
    }
  }, [stock, livePrice, baseRisk, open]);

  const handleTrade = async () => {
    setLoading(true);
    try {
      await submitIntradayTrade({
        symbol: stock.tradingsymbol,
        quantity,
        stopLossPrice: stopLoss,
        gttStopLossPrice: gttStopLoss,
        riskPerTrade,
        intradayScore: stock.score,
      });
      toast.success(`🚀 Trade executed for ${stock.tradingsymbol}`);
      onClose();
    } catch (err: any) {
      toast.error(`❌ Trade failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!stock) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
        Intraday Trade: {stock.tradingsymbol}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 2, bgcolor: "rgba(0,0,0,0.02)", borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography variant="body2">Intraday Score:</Typography>
            <Typography variant="body2" fontWeight="bold">{stock.score}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography variant="body2">Risk Factor (Score/100):</Typography>
            <Typography variant="body2" fontWeight="bold">{(stock.score / 100).toFixed(2)}x</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Risk for this Trade:</Typography>
            <Typography variant="body2" color="error.main" fontWeight="bold">
              ₹{riskPerTrade.toLocaleString()}
            </Typography>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            helperText={`Total Value: ₹${(quantity * livePrice).toLocaleString()}`}
          />
          <TextField
            fullWidth
            label="Primary SL (Level)"
            type="number"
            value={stopLoss}
            onChange={(e) => setStopLoss(Number(e.target.value))}
            helperText="Current SL-M price"
          />
          <TextField
            fullWidth
            label="Emergency GTT (Level)"
            type="number"
            value={gttStopLoss}
            onChange={(e) => setGttStopLoss(Number(e.target.value))}
            color="error"
            helperText="Disaster Backup (GTT)"
          />
        </Stack>

        <Box sx={{ mt: 2, p: 1, border: "1px dashed #ef4444", borderRadius: 1 }}>
          <Typography variant="caption" color="error">
             ⚠️ This will place 3 orders: 
             1. Market Entry 2. SL-M 3. Emergency GTT
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrade}
          disabled={loading || quantity <= 0}
        >
          {loading ? "Executing..." : "Confirm Trade"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
